import { useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CatalogueCard, CardGrid } from '../../components/Catalogue';
import { useCatalogueProfiles } from '../../hooks/useCatalogueProfiles';
import { NewPagination } from '../../components/Common/Pagination/Pagination';

interface CatalogueProfilesProps {
  onItemCountChange?: (count: number) => void;
}

const CatalogueProfiles = forwardRef<any, CatalogueProfilesProps>(({ onItemCountChange }, ref) => {
  const navigate = useNavigate();
  
  const { 
    profiles,
    loading, 
    approveProfile, 
    suspendProfile, 
    approveAllProfiles, 
    pagination 
  } = useCatalogueProfiles();

  // Notify parent of item count changes
  useEffect(() => {
    if (onItemCountChange) {
      onItemCountChange(profiles.length);
    }
  }, [profiles.length, onItemCountChange]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleApproveAll: approveAllProfiles,
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, overflow: 'hidden' }}>
      {/* Profile Cards Grid */}
      {profiles.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography>No profiles pending approval</Typography>
        </Box>
      ) : (
        <CardGrid minCardWidth="332px">
          {profiles.map((profile) => (
            <CatalogueCard
              key={profile.vendorId}
              id={profile.vendorId}
              image={profile.imageURL}
              subtitle={profile.businessName}
              title={profile.businessName}
              category={profile.category}
              tags={profile.tags}
              variant="profile"
              onCardClick={(id) => navigate(`/catalogue/profiles/${id}`)}
              onApprove={approveProfile}
              onSuspend={suspendProfile}
            />
          ))}
        </CardGrid>
      )}

      <NewPagination
        {...pagination}
      />
    </Box>
  );
});

CatalogueProfiles.displayName = 'CatalogueProfiles';

export default CatalogueProfiles;
